import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PermissionRequest } from '@claude-remote/shared';
import { Button } from '../components/Button.js';
import { ToolInputView } from '../components/ToolInputView.js';
import { AskUserQuestionCard } from '../components/AskUserQuestionCard.js';
import { api } from '../lib/api.js';
import { ws } from '../lib/ws.js';
import { buildTitle, useDocumentTitle } from '../lib/use-document-title.js';

interface PermissionRow {
  id: string;
  session_id: string;
  tool_name: string;
  tool_input: string;
  status: string;
  created_at: number;
  timeout_at: number | null;
  project_name: string;
  session_title: string | null;
}

export function InboxScreen() {
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  useDocumentTitle(buildTitle('Входящие'));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get<PermissionRow[]>('/api/v1/permissions');
      setPermissions(data);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
    const unsub = ws.onMessage((msg) => {
      if (msg.type === 'permission_request') {
        setPermissions((prev) => [permissionToRow(msg.request), ...prev]);
      } else if (msg.type === 'permission_resolved') {
        setPermissions((prev) => prev.filter((p) => p.id !== msg.requestId));
      }
    });
    return unsub;
  }, [load]);

  // Deep link из push: /inbox?permissionId=...&decision=approve|deny
  useEffect(() => {
    const url = new URL(window.location.href);
    const id = url.searchParams.get('permissionId');
    const decision = url.searchParams.get('decision');
    if (id && (decision === 'approve' || decision === 'deny')) {
      void decide(id, decision).finally(() => {
        url.searchParams.delete('permissionId');
        url.searchParams.delete('decision');
        window.history.replaceState({}, '', url.toString());
      });
    }
  }, []);

  // BroadcastChannel из SW — подхватываем approve/deny.
  useEffect(() => {
    let bc: BroadcastChannel | null = null;
    try {
      bc = new BroadcastChannel('claude-remote');
    } catch {
      return;
    }
    bc.addEventListener('message', (e) => {
      const data = e.data as { type?: string; permissionId?: string; decision?: string };
      if (data?.type === 'permission_decision' && data.permissionId && data.decision) {
        void decide(data.permissionId, data.decision as 'approve' | 'deny');
      }
    });
    return () => bc?.close();
  }, []);

  async function decide(id: string, decision: 'approve' | 'deny') {
    try {
      await api.post(`/api/v1/permissions/${id}/${decision}`);
      setPermissions((prev) => prev.filter((p) => p.id !== id));
    } catch {
      /* ignore */
    }
  }

  // AskUserQuestion: пользователь уже выбрал/написал ответ в карточке —
  // POST'им как approve с body.answer. Тот же путь, что у Session.tsx.
  async function answer(id: string, text: string) {
    try {
      await api.post(`/api/v1/permissions/${id}/approve`, { answer: text });
      setPermissions((prev) => prev.filter((p) => p.id !== id));
    } catch {
      /* ignore */
    }
  }

  // Группируем по проекту, сохраняя порядок «свежие первыми»: список уже
  // отсортирован сервером по created_at DESC, поэтому первым в группе
  // оказывается самый свежий запрос — по нему ставим группу в общем порядке.
  const groups = useMemo(() => {
    const order: string[] = [];
    const map = new Map<string, PermissionRow[]>();
    for (const p of permissions) {
      const key = p.project_name;
      if (!map.has(key)) {
        map.set(key, []);
        order.push(key);
      }
      map.get(key)!.push(p);
    }
    return order.map((name) => ({ name, items: map.get(name)! }));
  }, [permissions]);

  return (
    <div className="p-4 space-y-6 max-w-2xl mx-auto">
      <h1 className="text-2xl font-semibold pt-2">Inbox</h1>

      {loading ? (
        <p className="text-neutral-500 text-sm">Загружаем…</p>
      ) : permissions.length === 0 ? (
        <div className="rounded-2xl border border-neutral-800 p-6 text-center">
          <p className="text-neutral-400">Нет активных запросов</p>
          <p className="text-xs text-neutral-500 mt-1">
            Когда агент запросит разрешение — он появится здесь и прилетит пуш.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map((g) => (
            <section key={g.name} className="space-y-3">
              <h2 className="text-2xl md:text-3xl font-extrabold uppercase tracking-tight text-amber-200 break-words">
                {g.name}
                {g.items.length > 1 && (
                  <span className="ml-2 align-middle text-sm font-medium text-amber-100/70">
                    · {g.items.length} запроса
                  </span>
                )}
              </h2>
              <ul className="space-y-3">
                {g.items.map((p) => {
                  const isAsk = p.tool_name === 'AskUserQuestion';
                  return (
                    <li
                      key={p.id}
                      className="rounded-2xl border border-amber-600/40 bg-amber-500/10 p-4 space-y-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium">🔧 {p.tool_name}</div>
                          {p.session_title && (
                            <div className="text-sm text-neutral-300 mt-0.5 break-words">
                              {p.session_title}
                            </div>
                          )}
                          <div className="text-xs text-neutral-500 mt-0.5">
                            {new Date(p.created_at).toLocaleString('ru-RU')}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(`/sessions/${p.session_id}`)}
                        >
                          В сессию
                        </Button>
                      </div>
                      {isAsk ? (
                        // Интерактивная карточка с радио/чекбоксами и
                        // submit'ом ответа. Approve/Deny ниже не нужны —
                        // у самой карточки свои «Ответить» / «Отменить».
                        <AskUserQuestionCard
                          request={rowToRequest(p)}
                          onAnswer={(text) => void answer(p.id, text)}
                          onDeny={() => void decide(p.id, 'deny')}
                        />
                      ) : (
                        <>
                          <ToolInputView
                            toolName={p.tool_name}
                            toolInput={p.tool_input}
                          />
                          <div className="flex gap-2">
                            <Button
                              variant="danger"
                              className="flex-1"
                              onClick={() => void decide(p.id, 'deny')}
                            >
                              Deny
                            </Button>
                            <Button
                              className="flex-1"
                              onClick={() => void decide(p.id, 'approve')}
                            >
                              Approve
                            </Button>
                          </div>
                        </>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

function permissionToRow(req: PermissionRequest): PermissionRow {
  return {
    id: req.id,
    session_id: req.sessionId,
    tool_name: req.toolName,
    tool_input: req.toolInput,
    status: req.status,
    created_at: req.createdAt,
    timeout_at: req.timeoutAt,
    project_name: req.projectName,
    session_title: req.sessionTitle,
  };
}

// Обратное преобразование snake → camel — для передачи в общий
// AskUserQuestionCard, который ждёт полный PermissionRequest DTO.
// Поля decidedByDeviceId/decisionReason/decidedAt в строке inbox'а
// не нужны (запрос ещё pending), кладём null'ы для типа.
function rowToRequest(row: PermissionRow): PermissionRequest {
  return {
    id: row.id,
    sessionId: row.session_id,
    toolName: row.tool_name,
    toolInput: row.tool_input,
    status: row.status as PermissionRequest['status'],
    decidedByDeviceId: null,
    decisionReason: null,
    createdAt: row.created_at,
    decidedAt: null,
    timeoutAt: row.timeout_at,
    projectName: row.project_name,
    sessionTitle: row.session_title,
  };
}
